import React from 'react';
import { NewMapProps } from './types';
import { ArcPolyline, DestinationMarker, TakeOffMarker } from '@src/core/controllers';
import { arcWpsPath } from '@src/core/extensions';
import RouteWaypointLabel from '@src/features/order/pages/upsert-mission-page/components/route-waypoint-label';

const COINCIDENT_EPS = 1e-5; 

const toLatLng = (
  loc?: { latitude?: string | number | null; longitude?: string | number | null } | null,
): google.maps.LatLngLiteral | null => {
  if (!loc) return null;
  const lat = Number(loc.latitude);
  const lng = Number(loc.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const coincides = (a: google.maps.LatLngLiteral, b: google.maps.LatLngLiteral | null) =>
  !!b && Math.abs(a.lat - b.lat) < COINCIDENT_EPS && Math.abs(a.lng - b.lng) < COINCIDENT_EPS;

const NewMap: React.FC<NewMapProps> = (props) => {
  const { order } = props;

  if (!order) return null;

  const waypoints = order.waypoints ?? [];
  const routePath = arcWpsPath(waypoints);

  const takeoffPos = toLatLng(order.takeoffLocation) ?? routePath[0] ?? null;
  const destinationPos =
    toLatLng(order.deliveryLocation) ??
    toLatLng(waypoints.find((w) => Boolean(w.orderId))) ??
    routePath[routePath.length - 1] ??
    null;

  const path =
    routePath.length >= 2
      ? routePath
      : [takeoffPos, destinationPos].filter((p): p is google.maps.LatLngLiteral => Boolean(p));


  let counter = 0;
  const numberedWaypoints = waypoints.reduce<
    { position: google.maps.LatLngLiteral; waypointNumber: number }[]
  >((acc, wp) => {
    if (wp.orderId) return acc;
    const position = toLatLng(wp);
    if (!position) return acc;
    if (coincides(position, takeoffPos) || coincides(position, destinationPos)) return acc;
    counter += 1;
    acc.push({ position, waypointNumber: counter });
    return acc;
  }, []);

  return (
    <React.Fragment>
      <ArcPolyline.default path={path} />

      {takeoffPos && <TakeOffMarker.confirmed position={takeoffPos} />}
      {destinationPos && <DestinationMarker.confirmed position={destinationPos} />}

      {numberedWaypoints.map(({ position, waypointNumber }) => (
        <RouteWaypointLabel
          key={`${position.lat},${position.lng}-${waypointNumber}`}
          position={position}
          waypointNumber={waypointNumber}
        />
      ))}
    </React.Fragment>
  );
};

export default NewMap;
